import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { EsraPipingAPIClient, DesignCodeViewModel } from '../../../shared/models/autogenerated-piping';

export interface PropertySelectionData {
  currentValue: string;
  currentEdition?: string;
  title: string;
  propertyType: 'RatingClass' | 'DesignCode';
  editionFieldLabel?: string;
  searchFieldLabel?: string;
}

@Component({
  selector: 'app-property-selection-modal',
  templateUrl: './property-selection-modal.component.html',
  styleUrls: ['./property-selection-modal.component.scss'],
  standalone: false
})
export class PropertySelectionModalComponent implements OnInit {
  editionControl = new FormControl('2020', Validators.required);
  searchControl = new FormControl('');
  selectedValue = new FormControl('', Validators.required);

  propertyOptions: string[] = [];
  filteredOptions: string[] = [];
  loading = false;
  error: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<PropertySelectionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PropertySelectionData,
    private esraPipingAPIClient: EsraPipingAPIClient
  ) {
    if (data.currentValue) {
      this.selectedValue.setValue(data.currentValue);
    }

    if (data.currentEdition) {
      this.editionControl.setValue(data.currentEdition);
    }
  }

  ngOnInit(): void {
    this.loadPropertyOptions();



    // Setup search functionality for additional search field (principalmente para Design Code)
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.filterOptions();
    });
  }

  private loadPropertyOptions(): void {
    this.loading = true;
    this.error = null;

    if (this.data.propertyType === 'RatingClass') {

      this.esraPipingAPIClient.getDistinctActiveValues('RatingClass')
        .subscribe({
          next: (list: string[]) => {
            this.propertyOptions = list.sort();
            this.filteredOptions = [...this.propertyOptions];
            this.loading = false;
          },
          error: (error) => {
            console.error('Error loading rating class options:', error);
            this.error = 'Failed to load rating class options. Please try again.';
            this.loading = false;
          }
        });
    } else if (this.data.propertyType === 'DesignCode') {

      this.esraPipingAPIClient.getDistinctDesignCodes()
        .subscribe({
          next: (codes: DesignCodeViewModel[]) => {
            this.propertyOptions = codes.map(x => x.value).filter(x => x).sort();
            this.filteredOptions = [...this.propertyOptions];
            this.loading = false;
          },
          error: (error) => {
            console.error('Error loading design codes:', error);
            this.error = 'Failed to load design codes. Please try again.';
            this.loading = false;
          }
        });
    }
  }  private filterOptions(): void {
    const edition = this.editionControl.value?.toLowerCase() || '';
    const search = this.searchControl.value?.toLowerCase() || '';

    if (!edition && !search) {
      this.filteredOptions = [...this.propertyOptions];
    } else {
      this.filteredOptions = this.propertyOptions.filter(option => {
        const matchesEdition = !edition || option.toLowerCase().includes(edition);
        const matchesSearch = !search || option.toLowerCase().includes(search);
        return matchesEdition && matchesSearch;
      });
    }
  }

  onSelectionChange(value: string): void {
    this.selectedValue.setValue(value);
  }

  onConfirm(): void {
    if (this.selectedValue.valid && this.selectedValue.value) {
      this.dialogRef.close({
        value: this.selectedValue.value,
        edition: this.editionControl.value,
        propertyType: this.data.propertyType
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  retry(): void {
    this.loadPropertyOptions();
  }

  clearSearch(): void {
    this.editionControl.setValue('2020');
    this.searchControl.setValue('');
  }

  // Computed properties para labels dinÃ¡micos
  get editionFieldLabel(): string {
    return this.data.editionFieldLabel || 'Edition';
  }

  get searchFieldLabel(): string {
    return this.data.searchFieldLabel || 'Search';
  }

  get editionHint(): string {
    if (this.data.propertyType === 'RatingClass') {
      return 'Filter rating class options by edition';
    }
    return 'Filter design codes by edition';
  }

  get searchHint(): string {
    if (this.data.propertyType === 'RatingClass') {
      return 'Search through rating class options';
    }
    return 'Search through design codes';
  }

  get optionsCountLabel(): string {
    const propertyName = this.data.propertyType === 'RatingClass' ? 'Rating Class Options' : 'Design Code Options';
    return propertyName;
  }
}

